import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../select";
import React, { useState, useEffect } from "react";
import { Sheet, SheetTrigger, SheetContent, SheetClose } from "../sheet";
import { Calendar } from "../calendar";
import { Button } from "../button";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import * as Icons from "../../ui/shared/Icons";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAssistants } from "@/hooks/useAssistants";
import { useContactFilters } from "@/hooks/useContactFilters";
import { useTeam } from "@/hooks/useTeam";
import { Badge } from "../badge";

export interface ContactFilters {
  assistantId: string | null;
  attributionId: string | null;
  tags: string[];
  campaigns: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
}

interface MoreFilterProps {
  currentFilters?: ContactFilters;
  onFilterChange?: (filters: ContactFilters) => void;
  onClearFilters?: () => void;
}

const MoreFilter: React.FC<MoreFilterProps> = ({
  currentFilters = {
    assistantId: null,
    attributionId: null,
    tags: [],
    campaigns: [],
    dateFrom: null,
    dateTo: null
  },
  onFilterChange,
  onClearFilters
}) => {
  const { assistants, loading: loadingAssistants } = useAssistants();
  const { uniqueTags, uniqueCampaigns, loading: loadingFilters } = useContactFilters();
  const { members, loading: loadingMembers } = useTeam();

  const [localAssistantId, setLocalAssistantId] = useState<string>(currentFilters?.assistantId || '');
  const [localAttributionId, setLocalAttributionId] = useState<string>(currentFilters?.attributionId || '');
  const [localTag, setLocalTag] = useState<string>(currentFilters?.tags[0] || '');
  const [localCampaign, setLocalCampaign] = useState<string>(currentFilters?.campaigns[0] || '');
  const [localDateFrom, setLocalDateFrom] = useState<Date | undefined>(currentFilters?.dateFrom || undefined);
  const [localDateTo, setLocalDateTo] = useState<Date | undefined>(currentFilters?.dateTo || undefined);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (currentFilters) {
      setLocalAssistantId(currentFilters.assistantId || '');
      setLocalAttributionId(currentFilters.attributionId || '');
      setLocalTag(currentFilters.tags[0] || '');
      setLocalCampaign(currentFilters.campaigns[0] || '');
      setLocalDateFrom(currentFilters.dateFrom || undefined);
      setLocalDateTo(currentFilters.dateTo || undefined);
    }
  }, [currentFilters]);

  const handleApplyFilters = () => {
    if (!onFilterChange) {
      console.warn('⚠️ [MoreFilter] onFilterChange não está definido');
      setIsOpen(false);
      return;
    }

    const newFilters: ContactFilters = {
      assistantId: localAssistantId || null,
      attributionId: localAttributionId || null,
      tags: localTag ? [localTag] : [],
      campaigns: localCampaign ? [localCampaign] : [],
      dateFrom: localDateFrom || null,
      dateTo: localDateTo || null,
    };

    console.log('🔍 [MoreFilter] Aplicando filtros:', newFilters);
    onFilterChange(newFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    setLocalAssistantId('');
    setLocalAttributionId('');
    setLocalTag('');
    setLocalCampaign('');
    setLocalDateFrom(undefined);
    setLocalDateTo(undefined);

    if (onClearFilters) {
      onClearFilters();
    }
    setIsOpen(false);
  };

  const activeFiltersCount = currentFilters ? [
    currentFilters.assistantId,
    currentFilters.attributionId,
    currentFilters.tags.length > 0,
    currentFilters.campaigns.length > 0,
    currentFilters.dateFrom,
    currentFilters.dateTo
  ].filter(Boolean).length : 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger>
        <div className="border min-h-[40px] px-3 border-neutral-500 bg-neutral-600 hover:bg-neutral-500 transition-colors rounded-[6px] flex justify-center items-center gap-2 relative">
          <p className="text-neutral-50 font-medium text-[14px]">
            Mais filtros
          </p>
          <Icons.filter className="w-[16px] h-[16px]" />
          {activeFiltersCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-blue-600 hover:bg-blue-600 text-white text-xs">
              {activeFiltersCount}
            </Badge>
          )}
        </div>
      </SheetTrigger>
      <SheetContent className="bg-[#18181B] p-3 flex flex-col gap-4">
        <div className="w-full gap-2 flex justify-center items-center">
          <p className="text-[16px] font-medium text-neutral-100">
            Mais filtros
          </p>
          <Icons.filter
            className="w-[16px] h-[16px]"
            fill="#E4E4E7"
            stroke="stroke-[#E4E4E7]"
          />
        </div>

        {/* Assistente */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Assistente</label>
          <Select value={localAssistantId} onValueChange={setLocalAssistantId}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue placeholder={loadingAssistants ? "Carregando..." : "Selecione um assistente"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">Todos</SelectItem>
                {assistants.map((assistant) => (
                  <SelectItem key={assistant.id} value={assistant.id}>
                    {assistant.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Atribuído para */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Atribuído para</label>
          <Select value={localAttributionId} onValueChange={setLocalAttributionId}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue placeholder={loadingMembers ? "Carregando..." : "Selecione um responsável"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">Todos</SelectItem>
                {members
                  .filter((member) => member.auth_user_id)
                  .map((member) => (
                    <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                      {member.name}
                    </SelectItem>
                  ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Tag */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Tag</label>
          <Select value={localTag} onValueChange={setLocalTag}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue placeholder={loadingFilters ? "Carregando..." : uniqueTags.length === 0 ? "Nenhuma tag encontrada" : "Selecione uma tag"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">Todas</SelectItem>
                {uniqueTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Campanha */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Campanha</label>
          <Select value={localCampaign} onValueChange={setLocalCampaign}>
            <SelectTrigger className="w-full !text-neutral-50">
              <SelectValue placeholder={loadingFilters ? "Carregando..." : uniqueCampaigns.length === 0 ? "Nenhuma campanha encontrada" : "Selecione uma campanha"} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__clear__">Todas</SelectItem>
                {uniqueCampaigns.map((campaign) => (
                  <SelectItem key={campaign} value={campaign}>
                    {campaign}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Data Inicial */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Data Inicial</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-neutral-50 border border-[#3F3F464D] !bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateFrom && "text-muted-foreground"
                )}
              >
                {localDateFrom ? format(localDateFrom, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateFrom}
                onSelect={setLocalDateFrom}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Data Final */}
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">Data Final</label>
          <Popover>
            <PopoverTrigger
              className="!w-full !min-h-[56px] text-[14px] font-medium !text-neutral-50 border border-[#3F3F464D] !bg-[#18181B]"
              asChild
            >
              <Button
                variant={"outline"}
                className={cn(
                  "w-full flex justify-between text-left font-normal",
                  !localDateTo && "text-muted-foreground"
                )}
              >
                {localDateTo ? format(localDateTo, "PPP", { locale: ptBR }) : <span>Selecione uma data</span>}
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={localDateTo}
                onSelect={setLocalDateTo}
                initialFocus
                disabled={(date) => localDateFrom ? date < localDateFrom : false}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Buttons */}
        <div className="w-full flex gap-2 mt-auto">
          <Button
            onClick={handleClearFilters}
            variant="outline"
            className="flex-1 border-neutral-500 bg-transparent hover:bg-neutral-700 text-neutral-50"
          >
            <X className="w-4 h-4 mr-2" />
            Limpar
          </Button>
          <Button onClick={handleApplyFilters} className="flex-1">
            Filtrar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MoreFilter;
